// ./components/QueueScreen/TopBar.jsx
import React, { useRef, useState, useEffect } from "react";
import {
  Bookmark,
  CircleHelp,
  Maximize,
  Menu,
  Minimize,
  Moon,
  Settings as SettingsIcon,
  Sun,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

export default function TopBar({
  stationId,
  displayName,
  onToggleDarkMode,
  isDark,
  onOpenSettings,
  onOpenTutorial = null,
  onBumpAll,
  onExposeClick,
  showExposeButton = false,
  onSortClick,
  showSortButton = false,
  onPinClick,
  showPinButton = false,
  isPinActive = false,
  onToggleLockXAxis,
  isLockXAxisActive = false,
  onToggleLockYAxis,
  isLockYAxisActive = false,
  showAxisLockButtons = false,
}) {
  const { hasRole } = useAuth(); //  pulls from AuthProvider context

  // choose who can open settings
  const canOpenSettings = hasRole("settings_allow"); // adjust if you want expo/prep too
  const showTutorialButton = Boolean(onOpenTutorial);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (e) {
      console.warn("Fullscreen toggle failed:", e);
    }
  };

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const showFreeformActions =
    showExposeButton || showSortButton || showPinButton || showAxisLockButtons;
  const freeformActionButtonClass =
    "w-24 bg-slate-600 dark:bg-slate-500 text-white px-4 py-2 rounded hover:bg-slate-700 dark:hover:bg-slate-400 active:scale-[0.98] touch-manipulation select-none";
  const freeformToggleButtonClass = (isActive) =>
    `w-24 px-4 py-2 rounded active:scale-[0.98] touch-manipulation select-none transition-colors ${
      isActive
        ? "bg-orange-500 text-white hover:bg-orange-600"
        : "bg-slate-600 dark:bg-slate-500 text-white hover:bg-slate-700 dark:hover:bg-slate-400"
    }`;
  const headerIconButtonClass =
    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-blue-900 transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-300 dark:hover:bg-gray-700";

  return (
    <header className="relative grid h-12 grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 bg-white dark:bg-gray-800 border-b dark:border-gray-700 shadow-sm">
      <div className="flex items-center gap-2 text-xl text-blue-900 dark:text-blue-300 font-bold justify-self-start min-w-0">
        <Bookmark className="h-6 w-6 shrink-0" aria-hidden="true" />
        <span className="truncate">Station ID {stationId} - {displayName}</span>
      </div>

      <div className="flex items-center justify-center gap-2 justify-self-center">
        {showFreeformActions && (
          <>
            {showSortButton && (
              <button
                onClick={onSortClick}
                className={freeformActionButtonClass}
              >
                Sort
              </button>
            )}

            {showExposeButton && (
              <button
                onClick={onExposeClick}
                className={freeformActionButtonClass}
              >
                Expose
              </button>
            )}

            {showPinButton && (
              <button
                onClick={onPinClick}
                className={freeformToggleButtonClass(isPinActive)}
              >
                Pin
              </button>
            )}

            {showAxisLockButtons && (
              <button
                onClick={onToggleLockXAxis}
                className={freeformToggleButtonClass(isLockXAxisActive)}
              >
                Lock X
              </button>
            )}

            {showAxisLockButtons && (
              <button
                onClick={onToggleLockYAxis}
                className={freeformToggleButtonClass(isLockYAxisActive)}
              >
                Lock Y
              </button>
            )}
          </>
        )}
      </div>




      <div className="relative flex items-center justify-self-end gap-2" ref={menuRef}>

        {/* Tutorial Button */}
        {showTutorialButton && (
          <button
            type="button"
            onClick={onOpenTutorial}
            aria-label="Open Tutorial"
            className={headerIconButtonClass}
            title="MyODS Screens Tutorial"
          >
            <CircleHelp className="h-6 w-6" aria-hidden="true" />
          </button>
        )}
        {/*Toggle Dark/Light Mode*/}
        <button
          type="button"
          onClick={onToggleDarkMode}
          aria-label="Toggle light or dark mode"
          className={headerIconButtonClass}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? (
            <Moon className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Sun className="h-6 w-6" aria-hidden="true" />
          )}
        </button>

        {/* Fullscreen Button */}
        <button
          type="button"
          onClick={handleToggleFullscreen}
          aria-label="Toggle Fullscreen"
          className={headerIconButtonClass}
          title="Toggle Fullscreen"
        >
          {isFullscreen ? (
            <Minimize className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Maximize className="h-6 w-6" aria-hidden="true" />
          )}
        </button>

        {/* Settings/Menu Button */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Open station menu"
          className={headerIconButtonClass}
        >
          <Menu className="h-6 w-6" aria-hidden="true" />
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-28 z-50 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {/*  Only show Settings if allowed */}
            {canOpenSettings && (
              <button
                onClick={() => {
                  onOpenSettings();
                  setMenuOpen(false);
                }}
                className="w-full text-left px-5 py-4 text-base font-semibold text-gray-800 transition hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <span className="inline-flex items-center gap-2">
                  <SettingsIcon className="h-5 w-5" aria-hidden="true" />
                  Settings
                </span>
              </button>
            )}

            <button
              onClick={() => {
                setShowConfirm(true);
                setMenuOpen(false);
              }}
              className="w-full text-left px-5 py-4 text-base font-semibold text-gray-800 transition hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Bump All Tickets
            </button>
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-80">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Confirm Action
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to bump all tickets?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onBumpAll();
                  setShowConfirm(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
